import { Link } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import cx from 'classnames';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import RefreshPage from './RefreshPage';

interface FallBackProps {
  isFullWidth?: boolean;
  errorCode?: string;
  publicationPresent?: boolean;
  customTitle?: string;
  customDescription?: ReactNode | string;
  error?: any;
}

const FallBack = ({
  isFullWidth = false,
  errorCode = '404',
  publicationPresent = true,
  customTitle,
  customDescription,
  error,
}: FallBackProps) => {
  const { t, i18n } = useTranslation();

  // Check for RefreshPageError - handle both raw string and dev mode JSON wrapper
  const isRefreshPageError =
    error?.message === 'RefreshPageError' || error?.message?.includes('"message":"RefreshPageError"');

  if (isRefreshPageError) {
    return <RefreshPage isFullWidth={isFullWidth} />;
  }

  const title = i18n.exists(`errors.${errorCode}.title`) ? t(`errors.${errorCode}.title`) : t('errors.fallback.title');
  const description = i18n.exists(`errors.${errorCode}.description`)
    ? t(`errors.${errorCode}.description`)
    : t('errors.fallback.description');

  // tag exception as rendered in Fallback UI
  Sentry.setTag('render', 'fallback_ui_error');
  Sentry.captureRemixErrorBoundaryError(error);

  return (
    <div
      className={cx(
        'mx-auto flex min-h-screen items-center justify-center bg-white px-4 py-24',
        isFullWidth ? 'w-full' : 'max-w-3xl',
      )}
    >
      <div className={cx('mx-auto', isFullWidth ? 'max-w-screen-lg' : 'max-w-max')}>
        <main className="sm:flex">
          {errorCode !== '0' && <p className="text-4xl font-extrabold text-gray-600 sm:text-5xl">{errorCode}</p>}
          <div className="sm:ml-6">
            <div className={cx('sm:pl-6', errorCode !== '0' && 'sm:border-l sm:border-gray-200')}>
              <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
                {customTitle || title}
              </h1>

              <p className="mt-2 mb-2 text-base text-gray-500">{customDescription || description}</p>

              {publicationPresent && (
                <Link to="/" className="mt-1 text-base text-gray-600 underline offset-1 hover:text-gray-700">
                  {t('errors.back')}
                </Link>
              )}
              {error && (
                <details className="mt-4">
                  <summary className="text-sm font-medium text-gray-500">Error Details</summary>
                  <p className="mt-2 text-sm text-gray-900">{error.message}</p>
                  {error?.stack && (
                    <pre className="mt-2 w-[360px] overflow-x-auto text-sm text-gray-900 sm:w-[480px]">
                      {error.stack}
                    </pre>
                  )}
                </details>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default FallBack;
